import { Controller } from "@hotwired/stimulus"

// Product image picker: shows thumbnails of the chosen files before saving and
// makes the "Drag & Drop" area actually accept dropped images.
export default class extends Controller {
  static targets = ["input", "dropzone", "preview"]

  connect() {
    this.render()
  }

  async preview() {
    await this.optimize()
    this.render()
  }

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border", "border-primary")
  }

  dragleave() {
    this.dropzoneTarget.classList.remove("border", "border-primary")
  }

  async drop(event) {
    event.preventDefault()
    this.dragleave()

    const transfer = new DataTransfer()
    ;[...this.inputTarget.files, ...event.dataTransfer.files]
      .filter((file) => file.type.startsWith("image/"))
      .forEach((file) => transfer.items.add(file))

    this.inputTarget.files = transfer.files
    await this.optimize()
    this.render()
  }

  // Phone/camera photos are often several MB. Shrink big pictures in the
  // browser (max 1200px, WebP) so uploads are quick and product lists and the
  // POS load fast. GIFs and anything that can't be shrunk are left untouched.
  async optimize() {
    const transfer = new DataTransfer()
    for (const file of this.inputTarget.files) transfer.items.add(await this.shrink(file))
    this.inputTarget.files = transfer.files
  }

  async shrink(file) {
    const maxSide = 1200
    if (!file.type.startsWith("image/") || file.type === "image/gif") return file

    try {
      const bitmap = await createImageBitmap(file)
      const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height))
      if (scale === 1 && file.size < 500_000) return file

      const canvas = document.createElement("canvas")
      canvas.width = Math.round(bitmap.width * scale)
      canvas.height = Math.round(bitmap.height * scale)
      canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height)

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/webp", 0.85))
      if (!blob || blob.type !== "image/webp" || blob.size >= file.size) return file

      return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".webp", { type: "image/webp", lastModified: Date.now() })
    } catch {
      return file
    }
  }

  render() {
    const files = [...this.inputTarget.files]
    this.previewTarget.replaceChildren()

    files.forEach((file) => {
      const figure = document.createElement("div")
      figure.className = "text-center"
      figure.style.width = "84px"

      const img = document.createElement("img")
      img.src = URL.createObjectURL(file)
      img.className = "img-thumbnail"
      img.style.cssText = "width: 84px; height: 84px; object-fit: cover;"
      img.onload = () => URL.revokeObjectURL(img.src)

      const name = document.createElement("div")
      name.className = "text-muted text-truncate"
      name.style.fontSize = "11px"
      name.textContent = file.name

      figure.append(img, name)
      this.previewTarget.append(figure)
    })

    if (files.length > 0) {
      const note = document.createElement("p")
      note.className = "text-muted fs-13 w-100 mb-0"
      note.textContent = `${files.length} image${files.length === 1 ? "" : "s"} selected. They are uploaded when you save the product.`
      this.previewTarget.append(note)
    }
  }
}
